import { login, setLoading, setError, logout, setUserData } from '../redux/authSlice'
import ictaApi from '../api/ictaApi'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch } from '../redux/hooks'
import { LoginResponse, IUser } from '../models/auth'

export default function useLogin() {
	const dispatch = useAppDispatch()
	const navigate = useNavigate()

	const loginHandler = async (email: string, password: string) => {
		dispatch(setLoading(true))

		try {
			const { data } = await ictaApi.post<LoginResponse>('/auth/login', { email, password })
			if (data.user) {
				localStorage.setItem('token', data.token)
				dispatch(
					setUserData({
						firstname: data.user.firstname,
						lastname: data.user.lastname,
						email: data.user.email,
						active: data.user.active,
						role: data.user.role,
						position: data.user.position,
						image: data.user.image
					})
				)
				dispatch(
					login({
						token: data.token,
						msg: 'Login Success'
					})
				)
			} else {
				dispatch(setError('Invalid Credentials'))
				alert('Credenciales Invalidas')
			}
		} catch (error) {
			dispatch(setError('Login Failed'))
			alert('Error de sistema al iniciar sesion')
		}

		dispatch(setLoading(false))
	}

	const logoutHandler = () => {
		dispatch(logout())
		dispatch(setUserData({} as IUser))
		localStorage.removeItem('token')
		navigate('/login')
	}

	const validateToken = async () => {
		const token = localStorage.getItem('token')

		if (!token) {
			return
		}

		try {
			const { data, status } = await ictaApi.get<LoginResponse>('/auth')
			if (data.user && status === 200) {
				dispatch(
					setUserData({
						firstname: data.user.firstname,
						lastname: data.user.lastname,
						email: data.user.email,
						active: data.user.active,
						role: data.user.role,
						position: data.user.position,
						image: data.user.image
					})
				)
				dispatch(
					login({
						token: token,
						msg: 'Token Validated'
					})
				)
			} else {
				dispatch(logout())
				localStorage.removeItem('token')
				navigate('/login')
			}
		} catch (error) {
			dispatch(setError('Invalid Token'))
		}
	}

	const forgotPasswordHandler = async (email: string) => {
		dispatch(setLoading(true))
		
		try {
			const { data } = await ictaApi.post('/auth/forgot-password', { email })
			
			// Verificar si hay errores en la respuesta (incluso con status 200)
			if (data.errors && data.errors.length > 0) {
				const firstError = data.errors[0]
				return {
					success: false,
					message: firstError.msg
				}
			}
			
			if (data.ok) {
				return {
					success: true,
					message: data.msg
				}
			} else {
				return {
					success: false,
					message: data.msg || 'Error al enviar email de recuperación'
				}
			}
		} catch (error: any) {
			// Manejar errores de validación de la API (para otros casos)
			if (error.response?.data?.errors && error.response.data.errors.length > 0) {
				const firstError = error.response.data.errors[0]
				return {
					success: false,
					message: firstError.msg
				}
			}
			
			const errorMessage = error.response?.data?.msg || 'Error de sistema al solicitar recuperación'
			return {
				success: false,
				message: errorMessage
			}
		} finally {
			dispatch(setLoading(false))
		}
	}

	const resetPasswordHandler = async (token: string, newPassword: string) => {
		dispatch(setLoading(true))
		
		try {
			const { data } = await ictaApi.post('/auth/reset-password', { token, newPassword })
			
			// Verificar si hay errores en la respuesta (incluso con status 200)
			if (data.errors && data.errors.length > 0) {
				const firstError = data.errors[0]
				return {
					success: false,
					message: firstError.msg
				}
			}
			
			if (data.ok) {
				return {
					success: true,
					message: data.msg
				}
			} else {
				return {
					success: false,
					message: data.msg || 'Error al restablecer contraseña'
				}
			}
		} catch (error: any) {
			// Manejar errores de validación de la API (para otros casos)
			if (error.response?.data?.errors && error.response.data.errors.length > 0) {
				const firstError = error.response.data.errors[0]
				return {
					success: false,
					message: firstError.msg
				}
			}
			
			const errorMessage = error.response?.data?.msg || 'Error de sistema al restablecer contraseña'
			return {
				success: false,
				message: errorMessage
			}
		} finally {
			dispatch(setLoading(false))
		}
	}

	return { 
		loginHandler, 
		logoutHandler, 
		validateToken, 
		forgotPasswordHandler, 
		resetPasswordHandler 
	}
}
